import { html, css } from "lit";
import { customElement, property, query, state } from "lit/decorators.js";
import { Grid, GridErrorEvent, TableTheme } from "@omegagrid/grid";
import { getThemeManager } from "@omegagrid/core";
import { BaseVisualization, ReportEvent, ReportErrorEvent, VisualizationOptions } from "./baseVisualization";
import { TableAdapter } from "./tableAdapter";
import { TableController } from "./tableController";
import { msg } from "@omegagrid/localize";
import { ContextData } from "@reporting/rp-types/analytics";
import { createWorkbook, exportXLSX } from "@omegagrid/plugin-xlsx";
import rp, { helpers, themeForActiveColorScheme } from '@reporting/rp-app-host';

@customElement('rp-visual-table')
export class TableVisualization extends BaseVisualization {

	static styles = css`
		:host {
			display: block;
			width: 100%;
			height: 100%;
			position: relative;
		}

		og-grid {
			position: absolute;
			inset: 0;
		}

		.msg {
			padding: 5px;
		}
	`;

	@property({type: Object})
	adapter: TableAdapter;

	@query('og-grid')
	grid: Grid;

	@state()
	empty = false;

	/**
	 * Table-wide theme override: undefined = use the server-resolved
	 * reportData.theme, a TableTheme = editor live preview, null = cleared.
	 */
	@property({attribute: false})
	tableTheme: TableTheme|null|undefined = undefined;

	/**
	 * Theme edit mode, set by the visualization editor: cell click actions
	 * (drill-down, dimension menu) are suppressed, clicks only inspect cells.
	 */
	@property({attribute: false})
	themeEditMode = false;

	private _controller: TableController;

	private get effectiveTheme(): TableTheme|null {
		return this.tableTheme === undefined ? (this.reportData?.theme ?? null) : this.tableTheme;
	}

	/** applies the effective theme onto the grid model, as a copy fixed up for the active application theme */
	private applyTableTheme() {
		const theme = this.effectiveTheme;
		if (!theme || !this.grid?.model) return;
		this.grid.model.applyTheme(themeForActiveColorScheme(theme), true);
	}

	private _onAppThemeChange = () => this.refreshGrid();

	/**
	 * Runtime state the user changes by hand on the grid. A refresh rebuilds
	 * a pristine model (the only way to re-apply a theme), so the state is
	 * captured before and restored after - the data does not change, the
	 * indexes stay stable. Column widths survive through view_options.
	 */
	private captureGridState() {
		const model = this.grid?.model;
		if (!model) return null;
		return {
			collapsedRows: model.rows.filter(row => row.collapsed).map(row => row.index),
			collapsedColumns: model.columns.filter(column => column.collapsed).map(column => column.index),
		};
	}

	private restoreGridState(state: ReturnType<TableVisualization['captureGridState']>) {
		const model = this.grid?.model;
		if (!state || !model) return;
		let rows = false, columns = false;
		for (const index of state.collapsedRows) {
			const row = model.rows[index];
			// collapse() also hides the descendants, the flag alone would not
			if (row && !row.collapsed) { row.collapse(); rows = true; }
		}
		for (const index of state.collapsedColumns) {
			const column = model.columns[index];
			if (column && !column.collapsed) { column.collapse(); columns = true; }
		}
		if (rows || columns) {
			this.grid.table.renderCells({vertical: rows, horizontal: columns});
			this.grid.rendering.updateSliders();
		}
	}

	/** refresh keeping the user's runtime grid state (collapse, widths) */
	private async refreshGrid() {
		if (!this.grid) return;
		const state = this.captureGridState();
		await this.grid.refresh();
		this.restoreGridState(state);
	}

	connectedCallback() {
		super.connectedCallback();
		getThemeManager().addEventListener('change', this._onAppThemeChange);
	}

	disconnectedCallback() {
		super.disconnectedCallback();
		getThemeManager().removeEventListener('change', this._onAppThemeChange);
	}

	updated(props: Map<string, any>) {
		if (props.has('adapter')) {
			this._controller = new TableController(this, this.adapter.reportContext);
		}

		// an active theme replaces the legacy per-row styles of already loaded
		// results - must be set before the refresh below rebuilds the cells
		if (this.adapter) this.adapter.suppressLegacyStyles = !!this.effectiveTheme;

		if (props.has('tableTheme') && !props.has('adapter')) {
			// rebuild a pristine model from the adapter; afterLoad then applies
			// the new effective theme (or nothing when cleared)
			this.refreshGrid();
		}

		if (this.adapter) this.empty = this.adapter.isEmpty;

		(window as any).__dump_grid = (serialize: boolean) => {
			console.log(serialize ? JSON.stringify(this.grid.getSourceData()) : this.grid.getSourceData());
		}
	}

	refresh(contextData?: ContextData) {
		super.refresh(contextData);
		if (this.adapter) this.adapter.suppressLegacyStyles = !!this.effectiveTheme;
		this.refreshGrid();
	}

	render = () => this.empty ? html`
		<div class="msg">
			<og-alert type="warning" .text="${msg('No data available')}"></og-alert>
		</div>
	` : html`
		<og-grid
			.adapter="${this.adapter}"
			@grid.error="${(e: GridErrorEvent) => this.dispatchEvent(new ReportErrorEvent(e.error))}"
			@grid.afterLoad="${() => {
				this.applyTableTheme();
				this.runScripts();
				this.dispatchEvent(new ReportEvent('load'));
			}}">
		</og-grid>
	`;

	layout() {
		this.grid?.layout();
	}

	getOptions(): VisualizationOptions|Promise<VisualizationOptions> {
		return this.createOptions({
			edit: true,
			exportTypes: [
				{type: 'xlsx', icon: 'file-xls'},
				{type: 'pdf', icon: 'file-pdf'}
			]
		});
	}

	async export(type: string) : Promise<any> {
		switch (type) {
			case 'xlsx': 
				return createWorkbook([this.grid.model], {
					exportHiddenRows: false,
					exportHiddenColumns: false,
					gridLines: false,
					defaultBorders: true,
					dropdownCells: 'displayValue',
				});
			case 'pdf':
				return this.grid.rendering.exportHTML();
		}
		return null;
	}

	async download(type: string) {
		switch (type) {
			case 'xlsx': 
				await exportXLSX(await this.export(type), 'test.xlsx');
				break;
			case 'pdf':
				const html = await this.grid.rendering.exportHTML();
				const data = new FormData();
				data.append("config", new Blob([JSON.stringify({
					"filename": "export.pdf",
					"items": [{
						"input": { "type": "html" },
						"output": { "name": "content" }
					}]
				})], { type: "application/json" }));
				data.append("content", new Blob([html], { type: "application/html" }));

				try {
					const { url } = await rp.ms('export').post<{url: string}>('export', data, {download: 0});
					helpers.downloadFromUrl(url, "export.pdf");
				} catch (error) {
					rp.host.showAlert({type: 'danger', content: 'Error exporting PDF'});
					rp.log.error(error);
				}

				break;
		}
	}

}
